import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, Clock, Calendar, Share2, Copy, Check } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import BLOG_ARTICLES from '../data/blogArticles';

function CodeBlock({ inline, children, ...props }) {
  const [copied, setCopied] = React.useState(false);
  if (inline) {
    return <code className="bg-overlay text-[#e2e8f0] px-1.5 py-0.5 rounded text-[13px] font-mono" {...props}>{children}</code>;
  }
  const text = String(children).replace(/\n$/, '');
  return (
    <div className="relative group/code my-5 -mx-2 md:mx-0">
      <div className="flex items-center justify-between px-4 py-2 bg-app border border-line border-b-0 rounded-t-xl">
        <span className="text-[11px] text-content-3 font-mono">code</span>
        <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
          className="text-[11px] flex items-center gap-1 text-content-3 hover:text-content-2 transition-colors font-mono">
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="bg-app border border-line border-t-0 rounded-b-xl p-4 overflow-x-auto">
        <code className="text-[13px] font-mono text-content-2 leading-relaxed" {...props}>{children}</code>
      </pre>
    </div>
  );
}

function useSEO(article) {
  useEffect(() => {
    if (!article) return;

    document.title = `${article.title} | OpenSov AI Blog`;

    const setMeta = (property, content) => {
      let el = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
      if (!el) {
        el = document.createElement('meta');
        if (property.startsWith('og:') || property.startsWith('article:') || property.startsWith('twitter:')) {
          el.setAttribute('property', property);
        } else {
          el.setAttribute('name', property);
        }
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', article.excerpt);
    setMeta('author', 'OpenSov AI Team');
    setMeta('og:title', article.title);
    setMeta('og:description', article.excerpt);
    setMeta('og:type', 'article');
    setMeta('og:url', `https://opensov.com/blog/${article.slug}`);
    setMeta('og:image', article.image);
    setMeta('article:published_time', article.date);
    setMeta('article:section', article.category);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', article.title);
    setMeta('twitter:description', article.excerpt);
    setMeta('twitter:image', article.image);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://opensov.com/blog/${article.slug}`);

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.excerpt,
      datePublished: article.date,
      author: { '@type': 'Organization', name: 'OpenSov AI Team' },
      publisher: {
        '@type': 'Organization', name: 'OpenSov AI',
        logo: { '@type': 'ImageObject', url: 'https://customer-assets.emergentagent.com/job_9c3ced01-7873-4460-b2ba-221ae55b6a5f/artifacts/palgt8p6_OpenSov%20Logo%20.png' },
      },
      image: article.image,
      url: `https://opensov.com/blog/${article.slug}`,
      mainEntityOfPage: { '@type': 'WebPage', '@id': `https://opensov.com/blog/${article.slug}` },
      articleSection: article.category,
      wordCount: article.content.split(/\s+/).length,
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'article-jsonld';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('article-jsonld');
      if (s) s.remove();
    };
  }, [article]);
}

export default function BlogArticle() {
  const { slug } = useParams();
  const article = BLOG_ARTICLES.find(a => a.slug === slug);
  const [shared, setShared] = React.useState(false);

  const articleIdx = BLOG_ARTICLES.findIndex(a => a.slug === slug);
  const nextArticle = BLOG_ARTICLES[(articleIdx + 1) % BLOG_ARTICLES.length];
  const prevArticle = articleIdx > 0 ? BLOG_ARTICLES[articleIdx - 1] : null;

  useSEO(article);

  if (!article) return <Navigate to="/blog" replace />;

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: article.title, url });
    } else {
      navigator.clipboard.writeText(url);
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-app text-content font-sans">
      <Navbar />

      <main className="pt-28 pb-20" data-testid="blog-article-page">
        <article className="max-w-3xl mx-auto px-6 lg:px-8" itemScope itemType="https://schema.org/BlogPosting">
          <Link to="/blog" className="inline-flex items-center gap-2 text-content-3 text-sm font-sora hover:text-content transition-colors mb-8" data-testid="back-to-blog">
            <ArrowLeft className="w-4 h-4" /> All articles
          </Link>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border text-emerald-500 border-emerald-500/25 bg-emerald-500/[0.08]">
              {article.category}
            </span>
            <span className="text-content-3 text-[12px] font-sora flex items-center gap-1.5">
              <Calendar className="w-3 h-3" />
              <time dateTime={article.date} itemProp="datePublished">
                {new Date(article.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </time>
            </span>
            <span className="text-content-3 text-[12px] font-sora flex items-center gap-1.5">
              <Clock className="w-3 h-3" /> {article.readTime}
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-[2.75rem] font-heading font-bold text-content tracking-tight leading-[1.15] mb-6" itemProp="headline" data-testid="article-title">
            {article.title}
          </h1>

          <p className="text-lg text-content-2 font-sora leading-relaxed mb-8" itemProp="description">
            {article.excerpt}
          </p>

          <div className="flex items-center justify-between py-5 border-y border-line mb-10">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center">
                <span className="text-[11px] font-mono font-bold text-emerald-500">OS</span>
              </div>
              <div>
                <p className="text-sm font-sora font-medium text-content" itemProp="author" itemScope itemType="https://schema.org/Organization">
                  <span itemProp="name">OpenSov AI Team</span>
                </p>
                <p className="text-[11px] text-content-3 font-sora">Engineering & Research</p>
              </div>
            </div>
            <button onClick={handleShare} className="flex items-center gap-2 px-3 py-2 rounded-lg text-content-3 hover:bg-overlay-hover hover:text-content-2 transition-colors text-sm font-sora" data-testid="share-article-btn">
              {shared ? <Check className="w-4 h-4 text-green-400" /> : <Share2 className="w-4 h-4" />}
              {shared ? 'Copied' : 'Share'}
            </button>
          </div>

          <div className="relative aspect-[16/9] rounded-2xl overflow-hidden border border-line mb-12">
            <img src={article.image} alt={article.title} className="w-full h-full object-cover" loading="eager" itemProp="image" />
          </div>

          <div className="prose-article" itemProp="articleBody">
            <ReactMarkdown remarkPlugins={[remarkGfm]}
              components={{
                code({ inline, children, ...props }) { return <CodeBlock inline={inline} {...props}>{children}</CodeBlock>; },
                p({ children }) { return <p className="mb-5 text-content-2 font-sora text-[15px] leading-[1.85]">{children}</p>; },
                h2({ children }) { return <h2 className="text-2xl font-heading font-bold text-content tracking-tight mt-12 mb-4">{children}</h2>; },
                h3({ children }) { return <h3 className="text-lg font-heading font-semibold text-content mt-8 mb-3">{children}</h3>; },
                ul({ children }) { return <ul className="list-disc list-outside ml-5 mb-5 space-y-2 text-content-2 font-sora text-[15px] marker:text-content-3">{children}</ul>; },
                ol({ children }) { return <ol className="list-decimal list-outside ml-5 mb-5 space-y-2 text-content-2 font-sora text-[15px]">{children}</ol>; },
                li({ children }) { return <li className="pl-1 leading-[1.75]">{children}</li>; },
                strong({ children }) { return <strong className="text-content font-semibold">{children}</strong>; },
                em({ children }) { return <em className="text-content-2">{children}</em>; },
                a({ children, href }) { return <a href={href} className="text-emerald-500 hover:underline underline-offset-2" target="_blank" rel="noopener noreferrer">{children}</a>; },
                blockquote({ children }) { return <blockquote className="border-l-2 border-emerald-500/30 pl-5 my-6 text-content-2 font-sora italic text-[15px]">{children}</blockquote>; },
                table({ children }) { return <div className="overflow-x-auto my-6 rounded-xl border border-line"><table className="w-full text-[14px]">{children}</table></div>; },
                th({ children }) { return <th className="bg-overlay px-4 py-3 text-left text-content-2 font-semibold border-b border-line text-[13px] font-sora">{children}</th>; },
                td({ children }) { return <td className="px-4 py-3 text-content-2 border-b border-white/[0.03] text-[14px] font-sora">{children}</td>; },
                hr() { return <hr className="border-line my-10" />; },
              }}>
              {article.content}
            </ReactMarkdown>
          </div>

          <div className="mt-16 pt-8 border-t border-line grid sm:grid-cols-2 gap-6">
            {prevArticle && (
              <Link to={`/blog/${prevArticle.slug}`} className="group p-5 rounded-xl border border-line hover:border-line-2 bg-overlay hover:bg-overlay transition-all" data-testid="prev-article">
                <p className="text-[10px] font-mono text-content-3 uppercase tracking-wider mb-2">Previous</p>
                <p className="text-sm font-heading font-semibold text-content-2 group-hover:text-content transition-colors leading-snug">{prevArticle.title}</p>
              </Link>
            )}
            <Link to={`/blog/${nextArticle.slug}`} className={`group p-5 rounded-xl border border-line hover:border-line-2 bg-overlay hover:bg-overlay transition-all ${!prevArticle ? 'sm:col-start-2' : ''}`} data-testid="next-article">
              <p className="text-[10px] font-mono text-content-3 uppercase tracking-wider mb-2">Next</p>
              <p className="text-sm font-heading font-semibold text-content-2 group-hover:text-content transition-colors leading-snug">{nextArticle.title}</p>
            </Link>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}
